import React, { useEffect, useRef } from "react";
import axios from "axios";
import {
  useInfiniteQuery,
  QueryFunctionContext,
  InfiniteData,
} from "@tanstack/react-query";
import API_BASE_URL from "../config/env";
import LeadCard from "./LeadCard";

interface Item {
  created_at: string | number | Date;
  preferred_cities: string;
  config: string;
  id: number | null;
  raw_lead_id: number;
  name: string;
  email: string | null;
  is_valid: number | null;
  raw_lead_created_at: string;
  raw_leads_budget_max: number;
  raw_leads_budget_min: number;
  raw_leads_configuration: string;
  raw_leads_property_type : string;
  raw_leads_buying_location : string;
  customer_address : string;
  raw_leads_remarks : string;
  voice_note:string;
  last_disposition: string;

}

interface ApiResponse {
  items: Item[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  nextPage: number | null;
}

interface NotQualifyProps {
  onCountUpdate?: (count: number) => void;
  searchQuery?: string;
}

const NotQualify: React.FC<NotQualifyProps> = ({ onCountUpdate, searchQuery = "" }) => {
  const token = localStorage.getItem("authToken");
  
  const fetchItems = async (
    context: QueryFunctionContext<["notQualify", string], number>
  ): Promise<ApiResponse> => {
    const { pageParam = 1, queryKey } = context;
    const [, search] = queryKey;
    const headers = { Authorization: `Bearer ${token}` };
    
    let url = `${API_BASE_URL}/api/global-partner-connect/leads/not-qualified?page=${pageParam}&limit=10`;
    
    if (search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    
    const res = await axios.get<ApiResponse>(url, { headers });
    return res.data;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery<
    ApiResponse,
    Error,
    InfiniteData<ApiResponse>,
    ["notQualify", string],
    number
  >({
    queryKey: ["notQualify", searchQuery],
    queryFn: fetchItems,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.nextPage ?? undefined : undefined,
    initialPageParam: 1,
  });

  useEffect(() => {
    if (data?.pages?.[0]?.total !== undefined && onCountUpdate) {
      onCountUpdate(data.pages[0].total);
    }
  }, [data, onCountUpdate]);

  useEffect(() => {
    refetch();
  }, [searchQuery, refetch]);

  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) fetchNextPage();
    });

    const current = observerRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <p>Loading...</p>;
  if (isError) return <p>Error loading data</p>;

  return (
    <div className="lead-grid">
      {data?.pages.map((page, i) => (
        <React.Fragment key={i}>
          {page.items.map((item) => (
            <LeadCard
              key={item.raw_lead_id}
              id={item.id ?? 0}
              raw_lead_id={item.raw_lead_id ?? 0}
              cust_name={item.name}
              bhk={item.config || "-"}
              city={item.preferred_cities || "-"}
              raw_lead_created_at={new Date(item.raw_lead_created_at).toLocaleDateString()}
              time={new Date(item.raw_lead_created_at).toLocaleTimeString()} 
              status="notQualify"
              raw_leads_budget_max={item.raw_leads_budget_max}
              raw_leads_budget_min={item.raw_leads_budget_min}
              raw_leads_configuration={item.raw_leads_configuration}
              raw_leads_property_type={item.raw_leads_property_type}
              raw_leads_buying_location={item.raw_leads_buying_location}
              customer_address={item.customer_address}
              raw_leads_remarks={item.raw_leads_remarks}
              voice_note={item.voice_note}
              last_disposition={item.last_disposition}
            />
          ))}
        </React.Fragment>
      ))}

      <div ref={observerRef} style={{ height: 20 }} />

      {isFetchingNextPage && <p>Loading more...</p>}
      {!hasNextPage && data && data.pages[0].items.length > 0 && <p>No more data</p>}
      {data?.pages[0]?.items.length === 0 && <p>No leads found</p>}
    </div>
  );
};

export default NotQualify;